import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Switch } from 'components/Switch'
import { TooltipProps } from 'components/Tooltip'
import { isUnfilledRecoveryConfigured } from 'features/inventory/domain/forms/UnfilledRecoveryForm'
import { useAbility } from 'providers/casl'
import { useSwitchDomainProduct } from './hooks/useSwitchDomainProduct'
import { ProductSwitchCellProps } from './types'

export const ProductSwitchCell = ({
  disabled,
  getValue,
  product,
  row: {
    original: {
      domainUnfilledRecovery: { baseAdUnitPath },
      id,
      name,
    },
  },
}: ProductSwitchCellProps) => {
  const ability = useAbility()
  const { t: tDomain } = useTranslation('features/domain')
  const { t: tProduct } = useTranslation('features/product')

  const [checked, setChecked] = useState<boolean>(getValue() as boolean)
  const { errors, update } = useSwitchDomainProduct({ name, product, value: checked })

  const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)
  const isUnfilledRecoveryNotConfigured: boolean = !isUnfilledRecoveryConfigured(baseAdUnitPath)

  useEffect(() => {
    if (errors && errors.length > 0) {
      setChecked(getValue() as boolean)
    }
  }, [errors])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value: boolean = event.target.checked

    setChecked(value)
    void update({ id, product, value })
  }

  const composeValue = (): boolean => {
    if (noProductAccess) {
      return false
    } else if (product === 'unfilledRecovery' && isUnfilledRecoveryNotConfigured) {
      return false
    } else {
      return checked
    }
  }

  const composeDisabled = (): boolean => {
    if (product === 'unfilledRecovery') {
      return disabled || isUnfilledRecoveryNotConfigured
    } else {
      return disabled
    }
  }

  const composeTooltip = (): TooltipProps | undefined => {
    if (noProductAccess) {
      return {
        content: tProduct('featureUnavailable'),
      }
    } else if (product === 'unfilledRecovery' && isUnfilledRecoveryNotConfigured) {
      return {
        content: tDomain('form.unfilledRecovery.enabled.tooltip.notConfigured'),
      }
    } else {
      return undefined
    }
  }

  return (
    <Switch
      checked={composeValue()}
      disabled={composeDisabled()}
      name={product + 'Enabled'}
      onChange={handleChange}
      {...{
        tooltip: composeTooltip(),
        tooltipMode: 'wrap',
      }}
    />
  )
}
